import { ApiException } from 'common/exceptions/application/ApiException';
import { MeetingAlreadySavedException } from 'common/exceptions/application/MeetingAlreadySavedException';
import { EnsureEmailSubjectIsNotExcludedRequest } from 'common/models/EnsureEmailSubjectIsNotExcludedRequest';
import { EnsureEmailSubjectIsNotExcludedResponse } from 'common/models/EnsureEmailSubjectIsNotExcludedResponse';
import { FindDuplicateEmailOrMeetingResponse } from 'common/models/FindDuplicateEmailOrMeetingResponse';
import { FindDuplicateEmailRequest } from 'common/models/FindDuplicateEmailRequest';
import { FindDuplicateMeetingRequest } from 'common/models/FindDuplicateMeetingRequest';
import { GetClientCodeResponse } from 'common/models/GetClientCodeResponse';
import { GetClientListResponse } from 'common/models/GetClientListResponse';
import { GetLookupResponse } from 'common/models/GetLookupResponse';
import { LogRequest } from 'common/models/LogsRequest';
import { ProductAnalyticsTrackDTO } from 'common/models/ProductAnalyticsTrackDTO';
import {
  AutosaveContactDetailResponse,
  BulkCreateAutosaveContactRequest,
  CreateAutosaveContactRequest,
  UpdateAutosaveContactRequest,
} from 'common/models/SstkAutosaveContact';
import { UploadOutlookAttachmentResponse } from 'common/models/UploadOutlookAttachmentResponse';
import { UserContextDTO } from 'common/models/UserContextDTO';
import { ValidateFileRequest } from 'common/models/ValidateFileRequest';
import { Complaint } from '../models/Complaint';
import { GetDomainListResponse } from '../models/GetDomainListResponse';
import { Interaction } from '../models/Interaction';
import { LinkEntitiesRequest } from '../models/LinkEntitiesRequest';
import { Option } from '../models/Option';
import { SaveCommunicationResponse } from '../models/SaveInteractionResponse';
import { Stakeholder } from '../models/Stakeholder';
import { UploadOutlookAttachmentRequest } from '../models/UploadOutlookAttachmentRequest';
import { ApiResponse } from './../models/ApiResponse';
import { SaveEntityApiResponse } from './../models/SaveEntityApiResponse';
import * as Http from './http';
import * as UserSettings from './user-settings';

const TAG = 'API3: ';

export async function getDirectionFromServer(outlookItemDirection: string) {
  const response = await Http.get(
    UserSettings.getDomain() + '/common/directions'
  );

  const localizedDirections = response as [];

  const serverDirection = localizedDirections.find(
    (d: Option) => d.Text === outlookItemDirection
  );

  const direction = serverDirection || {
    Value: outlookItemDirection,
  };

  return direction;
}

export async function ensureEmailSubjectIsNotExcluded(
  request: EnsureEmailSubjectIsNotExcludedRequest
) {
  console.log(TAG, 'Checking if subject is in exclusion list', request);
  const response = (await Http.post<EnsureEmailSubjectIsNotExcludedResponse>(
    UserSettings.getDomain() +
      '/email-plugin-exclusion/ensure-email-subject-is-not-excluded',
    request
  )) as EnsureEmailSubjectIsNotExcludedResponse;

  if (!response.Success) {
    throw new ApiException(
      'Failed to check if subject is in exclusion list: ' + response.Message
    );
  }

  return response.IsSubjectExcluded;
}

export async function findDuplicateEmail(request: FindDuplicateEmailRequest) {
  console.log(TAG, 'Searching for duplicate email', request);
  const response = (await Http.post(
    UserSettings.getDomain() + '/email-duplicate-check/find-duplicate-email',
    request
  )) as FindDuplicateEmailOrMeetingResponse;

  if (!response.Success) {
    throw new ApiException(
      'Failed to search for duplicate email: ' + response.Message
    );
  }

  return response;
}

export async function findDuplicateMeeting(
  request: FindDuplicateMeetingRequest
) {
  console.log(TAG, 'Searching for duplicate meeting', request);
  const response = (await Http.post(
    UserSettings.getDomain() +
      '/meeting-duplicate-check/find-duplicate-meeting',
    request
  )) as FindDuplicateEmailOrMeetingResponse;

  if (!response.Success) {
    throw new ApiException(
      'Failed to search for duplicate meeting: ' + response.Message
    );
  }

  return response;
}

export async function saveInteraction(interaction: Interaction) {
  console.log(TAG, 'Saving interaction ', interaction);
  const response = await Http.post<SaveCommunicationResponse>(
    UserSettings.getDomain() + '/interaction/save',
    interaction
  );

  if (!response.Success) {
    if (response.ErrorCode === 'MeetingWithSameUidsAlreadyExists') {
      throw new MeetingAlreadySavedException();
    }
    throw new ApiException('Failed to save interaction: ' + response.Message);
  }

  return response.EntityID;
}

export async function saveComplaint(complaint: Complaint) {
  console.log(TAG, 'Saving complaint ', complaint);
  const response = await Http.post<SaveCommunicationResponse>(
    UserSettings.getDomain() + '/complaint/save',
    complaint
  );

  if (!response.Success)
    throw new ApiException('Failed to save complaint: ' + response.Message);

  return response.EntityID;
}

export async function linkInteractionToStakeholders(
  interactionId: string,
  stackHolderIds: string[]
) {
  const request: LinkEntitiesRequest = {
    LinkEntityType: 'InteractionToStakeholder',
    LinkID: interactionId,
    ToIDs: stackHolderIds,
    LinkPropertyAndStakholder: true,
  };
  await linkEntities(request);
}

export async function linkComplaintToStakeholders(
  complaintId: string,
  stackHolderIds: string[]
) {
  const request: LinkEntitiesRequest = {
    LinkEntityType: 'ComplaintToStakeholder',
    LinkID: complaintId,
    ToIDs: stackHolderIds,
    LinkPropertyAndStakholder: true,
  };
  await linkEntities(request);
}

export async function linkInteractionToAttachments(
  interactionId: string,
  attachmentIds: string[]
) {
  const request: LinkEntitiesRequest = {
    LinkEntityType: 'InteractionToFile',
    LinkID: interactionId,
    ToIDs: attachmentIds,
    LinkPropertyAndStakholder: true,
  };
  await linkEntities(request);
}

export async function linkComplaintToAttachments(
  interactionId: string,
  attachmentIds: string[]
) {
  const request: LinkEntitiesRequest = {
    LinkEntityType: 'ComplaintToFile',
    LinkID: interactionId,
    ToIDs: attachmentIds,
    LinkPropertyAndStakholder: true,
  };
  await linkEntities(request);
}

export async function linkEntities(request: LinkEntitiesRequest) {
  const response = await Http.post<ApiResponse>(
    UserSettings.getDomain() + '/link/linkentity',
    request
  );
  if (!response.Success)
    throw new ApiException(
      `Failed to link entities of type ${request.LinkEntityType}: ` +
        response.Message
    );
}

export async function validateAttachment(request: ValidateFileRequest) {
  const response = await Http.post<UploadOutlookAttachmentResponse>(
    UserSettings.getDomain() + '/file-v2/validate-file',
    request
  );

  if (!response.Success) {
    const fileInvalid =
      (response.Message as string).toLowerCase().indexOf('file is invalid') !==
      -1;
    if (fileInvalid) {
      console.log(TAG, 'Validate Attachment failed', JSON.stringify(response));
      return false;
    } else {
      throw new ApiException(
        'Failed to validate attachment: ' + response.Message
      );
    }
  }

  return true;
}

export async function uploadOutlookAttachments(
  request: UploadOutlookAttachmentRequest
) {
  const response = await Http.post<UploadOutlookAttachmentResponse>(
    UserSettings.getDomain() + '/file-v2/upload-outlook-attachment',
    request
  );

  if (!response.Success) {
    throw new ApiException(
      'Failed to process upload attachment response: ' + response.Message
    );
  }
  const responsesByAttachment = response.ResponsesByAttachment || [];
  const failedAttachments = responsesByAttachment
    .filter((a) => !a.IsSuccess)
    .map((a) => `${a.FileName} - (${a.Message}}) `);

  if (failedAttachments.length > 0) {
    const message =
      'Failed to upload File(s): ' + failedAttachments.join(', ') + +' \r\n';
    console.log(TAG, 'Uploading OutlookAttachments ', JSON.stringify(response));
    throw new ApiException(message);
  }

  return response;
}

export async function getContactGroups() {
  return await getOptions('/common/contactgroups');
}

export async function getConfidentialities() {
  return await getOptions('/common/confidentialities');
}

export async function getStatuses() {
  return await getOptions('/common/actionandcomplaintstatus');
}

export async function getUsers() {
  return await getOptions('/common/users');
}

export async function getInteractionTypes() {
  return await getOptions('/common/interaction-types');
}

export async function getInteractionTags() {
  const response = await Http.get<GetLookupResponse>(
    UserSettings.getDomain() + '/tags/Interaction',
    true
  );

  if (!response.Success) {
    return [];
  }
  return response.Values.map((i) => {
    return { Text: i.Text, Value: i.Id.toString() } as Option;
  });
}

export async function getPriorities() {
  return await getOptions('/common/priorities');
}

async function getOptions(url: string) {
  const response = await Http.get(UserSettings.getDomain() + url, true);
  if (!Array.isArray(response)) {
    return [];
  }
  return response as Option[];
}

export async function getClientCode() {
  const response = await Http.get<GetClientCodeResponse>(
    UserSettings.getDomain() + '/Domain/ClientCode',
    false
  );
  if (!response.Success || !response.ClientCode) {
    throw new ApiException('Failed to get client code: ' + response.Message);
  }
  return response.ClientCode;
}

export async function getDomainList(clientID: number) {
  console.log(
    TAG,
    'Requesting domain list ',
    UserSettings.getDomain(),
    UserSettings.getDomain() + `/domain/list/${clientID}`
  );
  const response = await Http.get<GetDomainListResponse>(
    UserSettings.getDomain() + `/domain/list/${clientID}`,
    true
  );

  return response;
}

export async function getClientList() {
  console.log(
    TAG,
    'Requesting client list ',
    UserSettings.getDomain(),
    UserSettings.getDomain() + '/client/list'
  );
  const response = await Http.get<GetClientListResponse>(
    UserSettings.getDomain() + '/client/list',
    true
  );

  return response;
}

export async function findStakeholder(stakeholder: Stakeholder) {
  let checkUrl = UserSettings.getDomain() + '/stakeholder/find?';
  if (stakeholder.FirstName != '') {
    checkUrl = checkUrl + 'firstName=' + stakeholder.FirstName + '&';
  }
  if (stakeholder.LastName != '') {
    checkUrl = checkUrl + 'lastName=' + stakeholder.LastName + '&';
  }
  checkUrl = checkUrl + 'email=' + stakeholder.Email;
  checkUrl = checkUrl + '&stakeholderType=' + stakeholder.StakeholderType;

  const response = await Http.get<string | ApiResponse>(checkUrl);

  if (response instanceof Object) {
    throw new ApiException(
      `Failed to search for stakeholder ${stakeholder.Email}: ` +
        response.Message
    );
  }

  return response as string;
}

export async function saveStakeholder(stakeholder: Stakeholder) {
  const response = await Http.post<SaveEntityApiResponse>(
    UserSettings.getDomain() + '/stakeholder/save',
    stakeholder
  );

  if (!response.Success) {
    throw new ApiException(
      `Failed to save stakeholder ${stakeholder.Email}: ` + response.Message
    );
  }

  return response.EntityID;
}

export async function mergeStakeholderInfo(stakeholder: Stakeholder) {
  const response = await Http.post<SaveEntityApiResponse>(
    UserSettings.getDomain() + '/stakeholder/mergeInfo',
    stakeholder
  );

  if (!response.Success) {
    throw new ApiException(
      `Failed to save stakeholder ${stakeholder.Email}: ` + response.Message
    );
  }
  return response.EntityID;
}

export async function getUserContext() {
  const response = await Http.get<UserContextDTO>(
    UserSettings.getDomain() + '/account/usercontext?detailed=true',
    true
  );

  return response;
}

export async function LogInfo(dto: LogRequest) {
  const success = await Http.post(UserSettings.getDomain() + '/log/info', dto);

  return success;
}

export async function LogError(dto: LogRequest) {
  const success = await Http.post(UserSettings.getDomain() + '/log/error', dto);

  return success;
}

export async function saveNewAutosaveContact(
  request: CreateAutosaveContactRequest
) {
  const response = await Http.post<SaveEntityApiResponse>(
    UserSettings.getDomain() + '/outlook-add-in/create-autosave-contact',
    request
  );

  if (!response.Success) {
    throw new ApiException(
      `Failed to save new autosave contact ${request.email}: ` +
        response.Message
    );
  }

  return response.EntityID;
}

export async function bulkSaveNewAutosaveContacts(
  request: BulkCreateAutosaveContactRequest
) {
  const response = await Http.post<SaveEntityApiResponse>(
    UserSettings.getDomain() + '/outlook-add-in/bulk-create-autosave-contact',
    request
  );

  if (!response.Success) {
    throw new ApiException(
      `Failed to bulk saving new autosave contact ` + response.Message
    );
  }

  return response.EntityID;
}

export async function updateAutosaveContact(
  request: UpdateAutosaveContactRequest
) {
  const response = await Http.post<SaveEntityApiResponse>(
    UserSettings.getDomain() + '/outlook-add-in/update-autosave-contact',
    request
  );

  if (!response.Success) {
    throw new ApiException(
      `Failed to update autosave contact ${request.email}: ` + response.Message
    );
  }

  return response.EntityID;
}

export async function getAutosaveContacts() {
  const response = await Http.get<AutosaveContactDetailResponse[]>(
    UserSettings.getDomain() + '/outlook-add-in/get-autosave-contacts'
  );

  // Convert date strings to Date objects
  return response.map(
    (item) =>
      ({
        ...item,
        AutosaveStartDate: new Date(item.AutosaveStartDate),
        LastSyncDate: new Date(item.LastSyncDate),
      } as AutosaveContactDetailResponse)
  );
}

export async function doesAutosaveContactExist(emailAddress: string) {
  return await Http.get<boolean>(
    UserSettings.getDomain() +
      '/outlook-add-in/does-autosave-contact-exist?emailAddress=' +
      encodeURIComponent(emailAddress)
  );
}

export async function getImageBase64(imageUrl: string) {
  return await Http.get<string>(
    UserSettings.getDomain() +
      '/outlook-add-in/outlook-image-to-base-64?imageUrl=' +
      encodeURIComponent(imageUrl)
  );
}

export async function deleteAutosaveContact(id: number) {
  const response = await Http.post<SaveEntityApiResponse>(
    UserSettings.getDomain() +
      '/outlook-add-in/delete-autosave-contact?id=' +
      encodeURIComponent(id.toString()),
    {}
  );

  if (!response.Success) {
    throw new ApiException(
      `Failed to delete autosave contact Id: ${id}: ` + response.Message
    );
  }

  return response.EntityID;
}

export async function findStakeholderV2(
  contact: AutosaveContactDetailResponse
) {
  let checkUrl = UserSettings.getDomain() + '/stakeholder/find?';
  if (contact.FirstName != '') {
    checkUrl = checkUrl + 'firstName=' + contact.FirstName + '&';
  }
  if (contact.LastName != '') {
    checkUrl = checkUrl + 'lastName=' + contact.LastName + '&';
  }
  checkUrl = checkUrl + 'email=' + contact.Email;
  checkUrl = checkUrl + '&stakeholderType=' + contact.StakeholderType;

  const response = await Http.get<string | ApiResponse>(checkUrl);

  if (response instanceof Object) {
    throw new ApiException(
      `Failed to search for stakeholder ${contact.Email}: ` + response.Message
    );
  }

  return response as string;
}

export async function isAutosaveContactMigrated(): Promise<boolean> {
  return await Http.get<boolean>(
    UserSettings.getDomain() + '/outlook-add-in/is-autosave-contact-migrated'
  );
}

export async function setAutosaveContactMigrateSuccess(): Promise<void> {
  await Http.post<void>(
    UserSettings.getDomain() +
      '/outlook-add-in/set-autosave-contact-migrate-success',
    {}
  );
}

export async function trackProductAnalyticsEvent(
  dto: ProductAnalyticsTrackDTO
): Promise<void> {
  const response = await Http.post<ApiResponse>(
    UserSettings.getDomain() + '/product-analytics/track-event',
    {
      eventName: dto.eventName,
      data: JSON.stringify(dto.data),
    }
  );

  if (!response.Success) {
    throw new ApiException(
      `Failed to track product analytics event: ${response.Message}`
    );
  }
}

// ── Organisation Enrichment ──────────────────────────────────────────────────

export enum EnumClientFeatureStatus {
  Disabled = 1,
  PendingEnable = 2,
  Enabled = 3,
}

export enum EnumClientFeatureAdditionalStatus {
  Disabled = 1,
  DetectRequiresApproval = 2,
  DetectAutoApprove = 3,
}

interface ClientFeatureDTO {
  FeatureSystemName: string;
  Status: EnumClientFeatureStatus;
  AdditionalStatus: EnumClientFeatureAdditionalStatus;
}

let cachedFeatures: ClientFeatureDTO[] | null = null;
let cachedFeaturesPromise: Promise<ClientFeatureDTO[]> | null = null;

async function getClientFeatures(): Promise<ClientFeatureDTO[]> {
  if (cachedFeatures) {
    return cachedFeatures;
  }

  if (!cachedFeaturesPromise) {
    // Wrap in Promise.resolve() to convert jQuery Deferred → native Promise
    // so jQuery's own handleError doesn't fire on rejection before our .catch()
    cachedFeaturesPromise = Promise.resolve(
      Http.get<ClientFeatureDTO[]>(
        UserSettings.getDomain() + '/client/features',
        true
      )
    )
      .then((response) => (Array.isArray(response) ? response : []))
      .then((features) => {
        cachedFeatures = features;
        return features;
      })
      .catch(() => {
        // Feature endpoint unavailable — default to enrichment disabled
        cachedFeaturesPromise = null;
        return [];
      });
  }

  return cachedFeaturesPromise;
}

export async function isOrganisationEnrichmentEnabled(): Promise<boolean> {
  const features = await getClientFeatures();
  const flag = features.find(
    (f) => f.FeatureSystemName === 'OrganisationDataEnrichment'
  );
  return flag?.Status === EnumClientFeatureStatus.Enabled;
}

export async function getOrganisationEnrichmentAdditionalStatus(): Promise<EnumClientFeatureAdditionalStatus> {
  const features = await getClientFeatures();
  const flag = features.find(
    (f) => f.FeatureSystemName === 'OrganisationDataEnrichment'
  );
  return flag?.AdditionalStatus ?? EnumClientFeatureAdditionalStatus.Disabled;
}

export async function getSuggestedOrganisationByEmail(
  email: string
): Promise<string | null> {
  const response = await Http.get<string>(
    UserSettings.getDomain() +
      '/stakeholder/get-suggested-organisation-by-email?email=' +
      encodeURIComponent(email)
  );
  return response || null;
}

export async function recordAiEnrichmentAction(dto: {
  EnrichmentType: number;
  EnrichmentContext: string;
  SuggestedValue: string;
  Action: number;
  ConfirmedValue?: string;
}): Promise<void> {
  await Http.post(UserSettings.getDomain() + '/ai-enrichment/record-action', dto);
}

export async function updateAiEnrichmentConfirmedValue(dto: {
  EnrichmentType: number;
  EnrichmentContext: string;
  ConfirmedValue: string;
}): Promise<void> {
  await Http.post(UserSettings.getDomain() + '/ai-enrichment/update-confirmed-value', dto);
}
